import { deleteDoublons } from "../doublon-tools"
import { normalizeString } from "../string"
import { ITreeNode } from "./interfaces"

// Helper functions for filtering
export const defaultMatcher = (
  filterText: string, // | string[],
  node: ITreeNode
): boolean => {
  const nodeName = node.name ? normalizeString(node.name) : ""
  const filter = normalizeString(filterText as string)
  return nodeName.includes(filter)
}

export const caracMatcher =
  (carac: string) =>
  (
    filterText: string, // | string[],
    node: ITreeNode
  ): boolean =>
    node.declinableCodeEnums?.includes(carac) || false

export const nodeExists = (
  node: ITreeNode,
  filter: string,
  matcher = defaultMatcher
): boolean => {
  return (
    matcher(filter, node) ||
    (node.children !== undefined &&
      !!node.children.find((child: ITreeNode) =>
        nodeExists(child, filter, matcher)
      ))
  )
}

export const filterTree = (
  node: ITreeNode,
  filter = "",
  matcher = defaultMatcher
): ITreeNode => {
  // If im an exact match then all my children get to stay
  if (matcher(filter, node) || !node.children) {
    return node
  }
  // If not then only keep the ones that match or have matching descendants
  const filtered: ITreeNode[] = node.children
    .filter((child) => nodeExists(child, filter, matcher))
    .map((child) => filterTree(child, filter, matcher))

  const result = { ...node, children: filtered }
  return result
}

export const expandFilteredNodes = (
  node: ITreeNode,
  filter: string | string[],
  callback: (nodesToExpand: string[]) => void,
  matcher = defaultMatcher
) => {
  if (typeof filter === "object") {
    const result = filter.reduce((acc: any, o) => {
      return deleteDoublons([...acc, ...recursiveExp(node, acc, o, matcher)])
    }, [])
    callback(result)
  } else {
    callback(recursiveExp(node, [], filter, matcher))
  }
}

const recursiveExp = (
  node: ITreeNode,
  nodesToExpand: string[],
  f: string,
  matcher: (s: string, node: ITreeNode) => boolean
) => {
  const children = node.children
  if (children && children.length !== 0) {
    nodesToExpand.push(node.value)
    const childrenWithMatches =
      node.children === undefined
        ? []
        : node.children.filter((child) => nodeExists(child, f, matcher))
    const shouldExpand = childrenWithMatches.length > 0
    if (shouldExpand) {
      childrenWithMatches.forEach((n) =>
        recursiveExp(n, nodesToExpand, f, matcher)
      )
    }
  }
  return nodesToExpand
}

// Single synchronous post-order pass. The former version created one promise per node and re-explored every
// subtree at each level to know whether it contained a match (FE-PERF-R10). Same result: a matching node keeps
// all its children; otherwise only the children that match or contain a match are kept.
export const multiFilterTree = ({
  node,
  filter = "",
  matchers = [defaultMatcher],
}: IMultiFilterTreeProps): Promise<ITreeNode> => {
  const keep = (n: ITreeNode) => matchers.every((matcher) => matcher(filter, n))
  const visit = (n: ITreeNode): ITreeNode | null => {
    if (keep(n)) {
      return n
    }
    if (!n.children) {
      return null
    }
    const children = n.children
      .map(visit)
      .filter((child): child is ITreeNode => child !== null)
    return children.length > 0 ? { ...n, children } : null
  }

  if (keep(node) || !node.children) {
    return Promise.resolve(node)
  }
  return Promise.resolve({
    ...node,
    children: node.children
      .map(visit)
      .filter((child): child is ITreeNode => child !== null),
  })
}

export const multiNodeExists = (
  node: ITreeNode,
  filter: string, // | string[],
  matchers = [defaultMatcher]
): boolean => {
  const keepNode = matchers.reduce(
    (acc, matcher) => acc && matcher(filter, node),
    true
  )
  return (
    keepNode ||
    (node.children !== undefined &&
      !!node.children.find((child: ITreeNode) =>
        multiNodeExists(child, filter, matchers)
      ))
  )
}

export interface IMultiFilterTreeProps {
  node: ITreeNode
  filter?: string // | string[]
  matchers?: Array<(f: string /*| string[]*/, node: ITreeNode) => boolean>
}
